import { useState, useRef, useEffect, useCallback } from 'react'
import Hls from 'hls.js'
import { 
  FiPlay, FiPause, FiVolume2, FiVolumeX, FiMaximize, FiMinimize, 
  FiSkipForward, FiSkipBack, FiSettings, FiLoader 
} from 'react-icons/fi'
import './BunnyVideoPlayer.css'

const BunnyVideoPlayer = ({ 
  url, 
  watermark, 
  onProgress, 
  onEnded,
  poster,
  autoPlay = false,
  isProcessing = false,
  processingReason = '',
  initialTime = 0
}) => {
  const videoRef = useRef(null)
  const containerRef = useRef(null)
  const hlsRef = useRef(null)
  const controlsTimeoutRef = useRef(null)
  
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [volume, setVolume] = useState(1)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [buffered, setBuffered] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [isHovering, setIsHovering] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showQualityMenu, setShowQualityMenu] = useState(false)
  const [qualities, setQualities] = useState([])
  const [currentQuality, setCurrentQuality] = useState(-1) // -1 = auto
  const [showScreenshotWarning, setShowScreenshotWarning] = useState(false)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [showSpeedMenu, setShowSpeedMenu] = useState(false)

  // Check if URL is a Bunny/HLS stream
  const isBunnyUrl = useCallback((videoUrl) => {
    if (!videoUrl) return false
    const u = videoUrl.toLowerCase()
    return u.includes('mediadelivery.net') || 
           u.includes('.m3u8') || 
           u.includes('bunnycdn') ||
           u.includes('b-cdn.net')
  }, [])

  // Initialize HLS player
  useEffect(() => {
    const video = videoRef.current
    if (!video || !url) return

    const initPlayer = () => {
      // Check if HLS is supported
      if (isBunnyUrl(url) && Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          backBufferLength: 90,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          startLevel: -1, // Auto quality
        })

        hls.loadSource(url)
        hls.attachMedia(video)

        hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
          setIsLoading(false)
          // Get available quality levels
          const levels = data.levels.map((level, index) => ({
            index,
            height: level.height,
            bitrate: level.bitrate,
            label: `${level.height}p`
          }))
          setQualities([{ index: -1, label: 'Auto' }, ...levels])
          
          if (autoPlay) {
            video.play().catch(() => {})
          }
        })

        hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
          setCurrentQuality(data.level)
        })

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                setError('Network error - retrying...')
                hls.startLoad()
                break
              case Hls.ErrorTypes.MEDIA_ERROR:
                setError('Media error - recovering...')
                hls.recoverMediaError()
                break
              default:
                setError('Failed to load video')
                hls.destroy()
                break
            }
          }
        })

        hlsRef.current = hls
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari)
        video.src = url
        video.addEventListener('loadedmetadata', () => {
          setIsLoading(false)
          if (autoPlay) {
            video.play().catch(() => {})
          }
        })
      } else {
        // Fallback for non-HLS URLs
        video.src = url
        video.addEventListener('loadedmetadata', () => {
          setIsLoading(false)
          if (autoPlay) {
            video.play().catch(() => {})
          }
        })
      }
    }

    initPlayer()

    return () => {
      // Cleanup HLS instance
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
      // Reset video element
      if (video) {
        video.pause()
        video.removeAttribute('src')
        video.load()
        setIsLoading(true)
        setError(null)
        setCurrentTime(0)
        setDuration(0)
        setBuffered(0)
        setIsPlaying(false)
      }
    }
  }, [url, autoPlay, isBunnyUrl])

  // Video event listeners
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const updateTime = () => {
      setCurrentTime(video.currentTime)
      if (onProgress && video.duration) {
        onProgress(video.currentTime / video.duration, video.duration)
      }
    }
    
    const updateDuration = () => {
      if (video.duration && !isNaN(video.duration)) {
        setDuration(video.duration)
        // Resume from saved position if initialTime is provided
        if (initialTime > 0 && initialTime < video.duration) {
          video.currentTime = initialTime
          setCurrentTime(initialTime)
        }
      }
    }
    
    const handleEnded = () => {
      setIsPlaying(false)
      if (onEnded) onEnded()
    }
    
    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    
    const handleWaiting = () => setIsLoading(true)
    const handleCanPlay = () => setIsLoading(false)
    
    const handleProgress = () => {
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1)
        setBuffered((bufferedEnd / video.duration) * 100)
      }
    }

    const handleError = () => {
      setError('Failed to load video')
      setIsLoading(false)
    }

    video.addEventListener('timeupdate', updateTime)
    video.addEventListener('loadedmetadata', updateDuration)
    video.addEventListener('ended', handleEnded)
    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('waiting', handleWaiting)
    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('progress', handleProgress)
    video.addEventListener('error', handleError)

    return () => {
      video.removeEventListener('timeupdate', updateTime)
      video.removeEventListener('loadedmetadata', updateDuration)
      video.removeEventListener('ended', handleEnded)
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('waiting', handleWaiting)
      video.removeEventListener('canplay', handleCanPlay)
      video.removeEventListener('progress', handleProgress)
      video.removeEventListener('error', handleError)
    }
  }, [onProgress, onEnded, initialTime])

  // Security: Screenshot/Print protection
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleBeforePrint = () => {
      container.style.filter = 'blur(20px)'
      setShowScreenshotWarning(true)
    }

    const handleAfterPrint = () => {
      container.style.filter = ''
      setShowScreenshotWarning(false)
    }

    const handleContextMenu = (e) => {
      e.preventDefault()
      return false
    }

    const handleKeyDown = (e) => {
      if (
        (e.ctrlKey && (e.key === 's' || e.key === 'S' || e.key === 'p' || e.key === 'P')) ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i')) ||
        e.key === 'F12' ||
        e.keyCode === 44
      ) {
        e.preventDefault()
        e.stopPropagation()
        return false
      }
    }

    window.addEventListener('beforeprint', handleBeforePrint)
    window.addEventListener('afterprint', handleAfterPrint)
    container.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('keydown', handleKeyDown, true)

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint)
      window.removeEventListener('afterprint', handleAfterPrint)
      container.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [])

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  // Playback rate change
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate
    }
  }, [playbackRate])

  // Controls visibility
  const handleMouseMove = useCallback(() => {
    setIsHovering(true)
    setShowControls(true)
    clearTimeout(controlsTimeoutRef.current)
    controlsTimeoutRef.current = setTimeout(() => {
      // In fullscreen we should auto-hide controls even if paused,
      // otherwise mouse may never "leave" to reset hovering.
      if (isFullscreen || isPlaying) {
        setShowControls(false)
        setIsHovering(false)
        setShowQualityMenu(false)
        setShowSpeedMenu(false)
      }
    }, 3000)
  }, [isPlaying, isFullscreen])

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false)
    if (isFullscreen || isPlaying) {
      setShowControls(false)
    }
    setShowQualityMenu(false)
    setShowSpeedMenu(false)
  }, [isPlaying, isFullscreen])

  // When entering fullscreen, start an auto-hide timer even if the mouse doesn't move.
  useEffect(() => {
    if (isFullscreen) {
      handleMouseMove()
    } else {
      // Leaving fullscreen: keep controls visible.
      clearTimeout(controlsTimeoutRef.current)
      setShowControls(true)
      setIsHovering(false)
    }

    return () => {
      clearTimeout(controlsTimeoutRef.current)
    }
  }, [isFullscreen, handleMouseMove])

  // Player controls
  const togglePlay = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    
    if (isPlaying) {
      video.pause()
    } else {
      video.play().catch(() => {})
    }
  }, [isPlaying])

  const toggleMute = useCallback(() => {
    setIsMuted(!isMuted)
    if (videoRef.current) {
      videoRef.current.muted = !isMuted
    }
  }, [isMuted])

  const handleVolumeChange = useCallback((e) => {
    const newVolume = parseFloat(e.target.value)
    setVolume(newVolume)
    setIsMuted(newVolume === 0)
    if (videoRef.current) {
      videoRef.current.volume = newVolume
      videoRef.current.muted = newVolume === 0
    }
  }, [])

  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen()
    } else {
      document.exitFullscreen()
    }
  }, [isFullscreen])

  const handleSeek = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const pos = (e.clientX - rect.left) / rect.width
    const newTime = pos * duration
    if (videoRef.current) {
      videoRef.current.currentTime = newTime
      setCurrentTime(newTime)
    }
  }, [duration])

  const skip = useCallback((seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, Math.min(duration, videoRef.current.currentTime + seconds))
    }
  }, [duration])

  const changeQuality = useCallback((levelIndex) => {
    if (hlsRef.current) {
      hlsRef.current.currentLevel = levelIndex
      setCurrentQuality(levelIndex)
    }
    setShowQualityMenu(false)
  }, [])

  const changeSpeed = useCallback((rate) => {
    setPlaybackRate(rate)
    setShowSpeedMenu(false)
  }, [])

  // Format time helper
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00'
    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const progress = duration ? (currentTime / duration) * 100 : 0
  const speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!containerRef.current?.contains(document.activeElement) && document.activeElement !== document.body) {
        return
      }
      
      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault()
          togglePlay()
          break
        case 'ArrowLeft':
          e.preventDefault()
          skip(-10)
          break
        case 'ArrowRight':
          e.preventDefault()
          skip(10)
          break
        case 'ArrowUp':
          e.preventDefault()
          handleVolumeChange({ target: { value: Math.min(1, volume + 0.1) } })
          break
        case 'ArrowDown':
          e.preventDefault()
          handleVolumeChange({ target: { value: Math.max(0, volume - 0.1) } })
          break
        case 'm':
          e.preventDefault()
          toggleMute()
          break
        case 'f':
          e.preventDefault()
          toggleFullscreen()
          break
        default:
          break
      }
    }

    document.addEventListener('keydown', handleKeyPress)
    return () => document.removeEventListener('keydown', handleKeyPress)
  }, [togglePlay, skip, handleVolumeChange, volume, toggleMute, toggleFullscreen])

  // If video is still processing, show processing state
  if (isProcessing) {
    return (
      <div ref={containerRef} className="bunny-video-player processing-state">
        <div className="video-processing">
          <FiLoader className="processing-spinner" />
          <h3>جاري معالجة الفيديو</h3>
          <p>الفيديو قيد المعالجة، يرجى المحاولة لاحقاً</p>
          {processingReason && <span className="processing-reason">{processingReason}</span>}
        </div>
        {watermark && (
          <div className="video-watermark">
            <span>{watermark}</span>
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={`bunny-video-player ${isFullscreen ? 'fullscreen' : ''}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      tabIndex={0}
    >
      <video
        ref={videoRef}
        className="video-element"
        playsInline
        poster={poster && poster.trim() ? poster : undefined}
        onClick={togglePlay}
        onDoubleClick={toggleFullscreen}
        controlsList="nodownload nofullscreen noremoteplayback"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        onError={(e) => {
          // Handle video loading errors gracefully
          const video = e.target
          if (video.error) {
            setError(`Failed to load video: ${video.error.message || 'Unknown error'}`)
            setIsLoading(false)
          }
        }}
        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
      />
      
      {/* Loading spinner */}
      {isLoading && (
        <div className="video-loading">
          <FiLoader className="loading-spinner" />
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="video-error">
          <p>{error}</p>
          <button onClick={() => { setError(null); window.location.reload() }}>
            Retry
          </button>
        </div>
      )}

      {/* Watermarks */}
      {watermark && (
        <div className="video-watermark">
          <span>{watermark}</span>
        </div>
      )}

      <div className="video-logo-watermark">
        <img 
          src="/logo.png" 
          alt="3afer" 
          draggable="false"
          onError={(e) => {
            // Hide logo if it fails to load (404)
            e.target.style.display = 'none'
          }}
        />
      </div>

      {/* Security overlay */}
      <div className="security-overlay">
        <div className="security-pattern"></div>
      </div>

      {/* Screenshot warning */}
      {showScreenshotWarning && (
        <div className="screenshot-warning">
          <div className="warning-content">
            <h3>⚠️ لقطات الشاشة غير مسموح بها</h3>
            <p>هذا المحتوى محمي</p>
          </div>
        </div>
      )}

      {/* Play button overlay */}
      {!isPlaying && !isLoading && !error && (
        <div className="play-button-overlay" onClick={togglePlay}>
          <div className="play-button-circle">
            <FiPlay />
          </div>
        </div>
      )}

      {/* Controls */}
      <div className={`video-controls ${showControls || isHovering ? 'visible' : ''}`}>
        {/* Progress bar */}
        <div className="progress-bar-container" onClick={handleSeek}>
          <div className="progress-bar">
            <div className="progress-buffered" style={{ width: `${buffered}%` }} />
            <div className="progress-filled" style={{ width: `${progress}%` }} />
            <div className="progress-handle" style={{ left: `${progress}%` }} />
          </div>
        </div>

        <div className="controls-bottom">
          <div className="controls-left">
            <button className="control-btn" onClick={togglePlay} title={isPlaying ? 'إيقاف' : 'تشغيل'}>
              {isPlaying ? <FiPause /> : <FiPlay />}
            </button>
            
            <button className="control-btn" onClick={() => skip(-10)} title="رجوع 10 ثواني">
              <FiSkipBack />
            </button>
            
            <button className="control-btn" onClick={() => skip(10)} title="تقديم 10 ثواني">
              <FiSkipForward />
            </button>
            
            <div className="volume-control">
              <button className="control-btn" onClick={toggleMute} title={isMuted ? 'تشغيل الصوت' : 'كتم الصوت'}>
                {isMuted || volume === 0 ? <FiVolumeX /> : <FiVolume2 />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="volume-slider"
              />
            </div>
            
            <div className="time-display">
              <span>{formatTime(currentTime)}</span>
              <span>/</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          <div className="controls-right">
            {/* Speed control */}
            <div className="speed-control">
              <button 
                className="control-btn" 
                onClick={() => { setShowSpeedMenu(!showSpeedMenu); setShowQualityMenu(false) }}
                title="سرعة التشغيل"
              >
                {playbackRate}x
              </button>
              {showSpeedMenu && (
                <div className="speed-menu">
                  {speedOptions.map((speed) => (
                    <button
                      key={speed}
                      className={`speed-option ${playbackRate === speed ? 'active' : ''}`}
                      onClick={() => changeSpeed(speed)}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Quality selector */}
            {qualities.length > 1 && (
              <div className="quality-control">
                <button 
                  className="control-btn" 
                  onClick={() => { setShowQualityMenu(!showQualityMenu); setShowSpeedMenu(false) }}
                  title="جودة الفيديو"
                >
                  <FiSettings />
                </button>
                {showQualityMenu && (
                  <div className="quality-menu">
                    {qualities.map((quality) => (
                      <button
                        key={quality.index}
                        className={`quality-option ${currentQuality === quality.index ? 'active' : ''}`}
                        onClick={() => changeQuality(quality.index)}
                      >
                        {quality.label}
                        {quality.index === -1 && currentQuality >= 0 && qualities[currentQuality + 1] && (
                          <span className="auto-quality"> ({qualities[currentQuality + 1]?.label})</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button className="control-btn" onClick={toggleFullscreen} title={isFullscreen ? 'خروج من ملء الشاشة' : 'ملء الشاشة'}>
              {isFullscreen ? <FiMinimize /> : <FiMaximize />}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BunnyVideoPlayer
